import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import type { TrailIncomeYoY as TrailIncomeYoYType } from '../../../types/acquisitionAnalysis';

interface TrailIncomeYoYProps {
  data: TrailIncomeYoYType[];
}

export const TrailIncomeYoY: React.FC<TrailIncomeYoYProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number | null) => {
    if (value === null) return '-';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">
          Trail Income - Year-over-Year Comparison
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Fiscal Year
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Period
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Trail Income
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  YoY Change
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.map((row, index) => (
                <tr key={index} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-white">
                    {row.fiscalYear}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">{row.period}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-indigo-400">
                    {formatCurrency(row.trailIncome)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {row.yoyChange !== null && (
                      <span
                        className={`inline-flex items-center gap-1 ${
                          row.yoyChange >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}
                      >
                        {row.yoyChange >= 0 ? (
                          <TrendingUp className="w-4 h-4" />
                        ) : (
                          <TrendingDown className="w-4 h-4" />
                        )}
                        {formatPercent(row.yoyChange)}
                      </span>
                    )}
                    {row.yoyChange === null && (
                      <span className="text-slate-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.length >= 2 && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <p className="text-sm text-emerald-300">
            <strong>Key Insight:</strong>{' '}
            {(() => {
              const latestRow = data[data.length - 1];
              const priorRow = data[data.length - 2];
              const latestChange = latestRow.yoyChange ?? 0;
              const priorChange = priorRow.yoyChange ?? 0;
              const isAccelerating = latestChange > priorChange + 2;
              const isDecelerating = latestChange < priorChange - 5;

              // Check if the latest row is a partial/YTD year
              const isPartialYear = !latestRow.period.includes('Jun');

              if (isPartialYear) {
                // For partial years (YTD), show acceleration comparison
                const changeSign = latestChange >= 0 ? '+' : '';
                return `Growth is ${isAccelerating ? 'accelerating' : isDecelerating ? 'decelerating' : 'stable'} - ${latestRow.fiscalYear} YTD is ${changeSign}${latestChange.toFixed(1)}% ahead of the same period last year.`;
              } else {
                // For complete years, show YoY comparison
                const changeSign = latestChange >= 0 ? '+' : '';
                return `Trail income grew ${changeSign}${latestChange.toFixed(1)}% year-over-year in ${latestRow.fiscalYear}.${priorChange > 0 ? ` Prior year saw ${priorChange >= 0 ? '+' : ''}${priorChange.toFixed(1)}% growth.` : ''}`;
              }
            })()}
          </p>
        </div>
      )}
    </div>
  );
};
